import React, { useState } from 'react';
import PageHeader from '../components/PageHeader';
import Reveal from '../components/Reveal';
import { Plus, Minus } from 'lucide-react';

const CATEGORIES = [
  { id: 'orders', label: 'Orders' },
  { id: 'shipping', label: 'Shipping' },
  { id: 'returns', label: 'Returns' },
  { id: 'product', label: 'Product Care' },
  { id: 'account', label: 'Account' },
];

const FAQS = {
  orders: [
    {
      q: 'Can I modify or cancel my order after placing it?',
      a: 'You can modify or cancel your order within one hour of placing it by emailing andobreeofficial1@gmail.com with your order number. After that, your order is already with our atelier for preparation.',
    },
    {
      q: 'Do you offer gift wrapping?',
      a: 'Every Andobr\u00e9e order is hand-wrapped in our signature linen as standard. For gifts, you can add a handwritten note at checkout \u2014 there is no additional cost.',
    },
    {
      q: 'When will my payment be charged?',
      a: 'Your payment is authorised at checkout and charged when your order ships from our atelier, usually within two working days.',
    },
  ],
  shipping: [
    {
      q: 'How much does shipping cost?',
      a: 'Standard shipping is complimentary on every order, worldwide, with no minimum. Express and next-day options are available at checkout where applicable.',
    },
    {
      q: 'Do you ship internationally?',
      a: 'Yes \u2014 we ship to over 60 countries. Any duties and taxes are calculated and shown transparently at checkout; there are no surprises on delivery.',
    },
    {
      q: 'Can I track my order?',
      a: 'Absolutely. The moment your order leaves our atelier, you will receive an email with a tracking link. You can also track directly from your account.',
    },
  ],
  returns: [
    {
      q: 'What is your return policy?',
      a: 'We offer free returns within 30 days of delivery on full-priced items. Pieces must be unworn, with original tags attached. Sale items are eligible for exchange or store credit.',
    },
    {
      q: 'How do I return an item?',
      a: 'Log in to your account, select the order, and press &ldquo;Return&rdquo;. We will email a pre-paid label within the hour. Drop the parcel at any post office.',
    },
    {
      q: 'When will I receive my refund?',
      a: 'Refunds are issued to your original payment method within five business days of your return arriving at our warehouse.',
    },
  ],
  product: [
    {
      q: 'How should I care for cashmere and wool?',
      a: 'We recommend professional dry cleaning. Between wearings, air the piece on a wide wooden hanger and store folded with tissue paper to preserve the fibres.',
    },
    {
      q: 'Can pieces be repaired?',
      a: 'Yes. As part of our lifetime repair programme, we will refresh seams, reline, replace buttons, and re-sole shoes \u2014 free of charge for the life of the piece.',
    },
    {
      q: 'Are sizes true to fit?',
      a: 'Our pieces are cut with a generous, draped silhouette. If you are between sizes, we typically recommend sizing down. Detailed measurements are on our size guide.',
    },
  ],
  account: [
    {
      q: 'Do I need an account to place an order?',
      a: 'You can check out as a guest. An account is optional, but it makes reorders, returns and wishlists easier to manage.',
    },
    {
      q: 'I&rsquo;ve forgotten my password, what should I do?',
      a: 'Use the &ldquo;Forgot password&rdquo; link on the login page and we will send a reset email within a few moments.',
    },
    {
      q: 'How do I unsubscribe from emails?',
      a: 'Every email we send has an unsubscribe link at the bottom. You can also manage preferences from your account settings.',
    },
  ],
};

const FAQ = () => {
  const [category, setCategory] = useState('orders');
  const [open, setOpen] = useState(null);

  const items = FAQS[category];

  return (
    <div>
      <PageHeader
        eyebrow="FAQ"
        title="Questions, answered."
        intro="A shorter version of our client-care handbook. If you can&rsquo;t find what you&rsquo;re looking for, our team is one email away."
      />

      <section className="pb-32 px-6 lg:px-12">
        <div className="max-w-[1100px] mx-auto grid md:grid-cols-[220px_1fr] gap-12">
          {/* Category nav */}
          <Reveal>
            <nav className="md:border-r md:border-neutral-200 md:pr-8 flex md:flex-col gap-1 overflow-x-auto">
              {CATEGORIES.map((c) => (
                <button
                  key={c.id}
                  onClick={() => {
                    setCategory(c.id);
                    setOpen(null);
                  }}
                  className={`text-left whitespace-nowrap px-4 py-3 text-[11px] tracking-[0.22em] uppercase transition-colors ${
                    category === c.id
                      ? 'text-neutral-900 bg-neutral-100'
                      : 'text-neutral-500 hover:text-neutral-900'
                  }`}
                >
                  {c.label}
                </button>
              ))}
            </nav>
          </Reveal>

          {/* Questions */}
          <div>
            {items.map((item, i) => {
              const isOpen = open === i;
              return (
                <Reveal key={item.q} delay={i * 80}>
                  <div className="border-b border-neutral-200">
                    <button
                      onClick={() => setOpen(isOpen ? null : i)}
                      className="w-full flex items-start justify-between gap-6 py-6 text-left"
                    >
                      <span className="font-display text-lg md:text-xl font-medium">{item.q}</span>
                      <span className="flex-shrink-0 mt-1">
                        {isOpen ? (
                          <Minus className="w-4 h-4" strokeWidth={1.5} />
                        ) : (
                          <Plus className="w-4 h-4" strokeWidth={1.5} />
                        )}
                      </span>
                    </button>
                    <div
                      className={`grid transition-all duration-500 ease-out ${
                        isOpen ? 'grid-rows-[1fr] opacity-100 pb-6' : 'grid-rows-[0fr] opacity-0'
                      }`}
                    >
                      <div className="overflow-hidden">
                        <p
                          className="text-[15px] leading-relaxed text-neutral-600 max-w-2xl"
                          dangerouslySetInnerHTML={{ __html: item.a }}
                        />
                      </div>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};

export default FAQ;
